import React from 'react';

import cn from 'classnames';

import { RequestMaterialStageContext } from '@entities/requestMaterial';
import { useAddRequestMaterialModal } from '@features/AddRequestMaterialModal/lib/hooks/useAddRequestMaterialModal';
import { DeliveryDateModal, ModalBody } from '@features/AddRequestMaterialModal/ui';
import { getPluralized } from '@shared/helpers';
import { CustomModal, Flex, Icon, Input, UiButton, UiText } from '@shared/ui';

import cls from './AddRequestMaterialModal.module.css';

interface AddRequestMaterialModalProps {
    classes?: string;
    isOpen: boolean;
    onClose: () => void;
}

export const AddRequestMaterialModal = ({
    classes,
    isOpen,
    onClose,
}: AddRequestMaterialModalProps) => {
    const {
        t,
        selectedGuids,
        context,
        search,
        onSearchChange,
        manualPositions,
        addManualPosition,
        validateAndSubmit,
        isDeliveryDateModalOpen,
        onDeliveryDateSave,
        onDeliveryDateBack,
    } = useAddRequestMaterialModal(onClose);

    return (
        <RequestMaterialStageContext.Provider value={context}>
            <CustomModal
                onHideModal={onClose}
                isOpen={isOpen && !isDeliveryDateModalOpen}
                contentClasses={cn(cls.AddRequestMaterialModal, [classes])}
            >
                <CustomModal.Header classes={cls.AddRequestMaterialHeader}>
                    <Flex direction="column" fullWidth gap={16} align="start">
                        {t('AddRequestMaterialModal.CustomModal.Header')}
                        <Flex gap={32} fullWidth>
                            <Input
                                leftIcon={<Icon iconName="search" height={16} width={16} />}
                                classes={[cls.searchInput]}
                                placeholder={t(
                                    'AddRequestMaterialModal.ModalBody.return.input.placeholder'
                                )}
                                value={search}
                                onValueChange={onSearchChange}
                            />
                            <UiButton
                                btnColor="secondary"
                                btnText={t(
                                    'AddRequestMaterialModal.ModalBody.return.UiButton.btnText'
                                )}
                                startIcon={
                                    <Icon
                                        iconName="plus"
                                        fill="currentColor"
                                        width={10}
                                        height={10}
                                    />
                                }
                                onClick={addManualPosition}
                            />
                        </Flex>
                    </Flex>
                </CustomModal.Header>
                <CustomModal.Body classes={cls.AddRequestMaterialBody}>
                    <ModalBody search={search} manualPositions={manualPositions} />
                </CustomModal.Body>
                <CustomModal.Footer>
                    <Flex justify="between" fullWidth>
                        <UiText>
                            {t('AddRequestMaterialModal.return.CustomModal.Footer.UiText')}{' '}
                            {getPluralized(selectedGuids.size, {
                                one: t(
                                    'AddRequestMaterialModal.return.CustomModal.Footer.UiText.getPluralized.one'
                                ),
                                few: t(
                                    'AddRequestMaterialModal.return.CustomModal.Footer.UiText.getPluralized.few'
                                ),
                                many: t(
                                    'AddRequestMaterialModal.return.CustomModal.Footer.UiText.getPluralized.many'
                                ),
                            })}
                        </UiText>

                        <Flex gap={12}>
                            <UiButton
                                btnColor="secondary"
                                onClick={onClose}
                                btnText={t('button.cancel.text')}
                            />
                            <UiButton
                                disabled={!selectedGuids.size}
                                btnText={t('button.create.text')}
                                onClick={validateAndSubmit}
                            />
                        </Flex>
                    </Flex>
                </CustomModal.Footer>
            </CustomModal>

            <DeliveryDateModal
                isOpen={isDeliveryDateModalOpen}
                onBack={onDeliveryDateBack}
                onSave={onDeliveryDateSave}
            />
        </RequestMaterialStageContext.Provider>
    );
};
